/**
 * MessageListPage - Main message list view
 *
 * Orchestrates:
 * - Header with create message button
 * - Filter bar
 * - Paginated message table
 * - WebSocket real-time updates
 * - Notification mark-as-read on mount
 */

import React, { useState, memo } from 'react';
import { useLocation } from 'react-router-dom';

import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { MessageListHeader, MessageListFilters, MessageListTable } from './index';

import useCompanionCase from 'modules/cases/hooks/useCompanionCase';
import { MessageListErrorBoundary } from '../ErrorBoundary';
import { useMessageContext } from '../../context';
import { CreateMessageModal } from '../CreateMessage';
import { useMessageList, useMessageFilters, useAuthors } from '../../hooks';

function MessageListPage() {
  const { isGlobal, isFromClientPortal, caseId, clientPortalDashboard } = useMessageContext();
  const { companionList } = useCompanionCase(caseId);
  const location = useLocation();

  // Create message modal state
  const searchParams = new URLSearchParams(location.search);
  const [showCreateModal, setShowCreateModal] = useState(
    location?.state?.createMessage || searchParams.get('createMessage') ? { companion_link: false } : false
  );

  // Filters & pagination
  const filterState = useMessageFilters({ isGlobal, isFromClientPortal });

  // Authors list
  const {
    authorsList,
    clientsList,
    isLoading: isAuthorLoading,
  } = useAuthors({
    caseId,
    isGlobal,
    enabled: !clientPortalDashboard,
  });

  // Message list query
  const { data: messageData, isLoading: isListLoading } = useMessageList(filterState.apiFilters, filterState.paginationState, {
    enabled: !clientPortalDashboard,
  });

  return (
    <>
      <GlobalLoader />
      <MessageListErrorBoundary onError={(error) => console.error('MessageList Error:', error)}>
        <div className="message-wrap px-2 pb-3">
          <MessageListHeader setShowCreateModal={setShowCreateModal} companionList={companionList} />

          <MessageListFilters
            filterState={filterState}
            authorsList={authorsList}
            clientsList={clientsList}
            isAuthorLoading={isAuthorLoading}
          />

          <MessageListTable
            messages={messageData?.messages || []}
            totalCount={messageData?.count || 0}
            currentPage={messageData?.currentPage || 1}
            totalPages={messageData?.totalPages || 1}
            isLoading={isListLoading}
            filterState={filterState}
          />

          {showCreateModal && (
            <CreateMessageModal
              show={showCreateModal}
              setShow={setShowCreateModal}
              companionLink={showCreateModal?.companion_link}
              companionList={companionList}
              createInAllCompanions={showCreateModal?.create_in_all_companions}
            />
          )}
        </div>
      </MessageListErrorBoundary>
    </>
  );
}

export default memo(MessageListPage);
